"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { T } from "@/i18n"
import { useGetTrigger, useCreateTrigger, useUpdateTrigger } from "@/hooks/triggers"
import {
  ENTITY_TYPES,
  RECIPIENT_TYPES,
  RECIPIENT_FIELD_OPTIONS,
  TriggerEntityType,
  Trigger,
} from "@/models/trigger"
import { useGetEmailTemplates } from "@/hooks/email-templates"
import { LoadingSpinner } from "@/components/loading-spinner"
import { ArrowLeft, Plus, Save, Trash } from "lucide-react"
import { ConditionBuilder } from "./condition-builder"
import { EmailTemplate } from "@/models/email-template"

type Condition = {
  id: string
  field: string
  currentValue: string | undefined
  beforeValue: string  | undefined
}

type RecipientType = "static" | "worker" | "dynamic" | "entity"

type Recipient = {
  type: RecipientType
  value: string
}

type TriggerFormData = {
  name: string
  description: string
  entityType: TriggerEntityType
  active: boolean
  conditions: Condition[]
  emailTemplateId: string
  recipients: Recipient[]
}

const initialFormData: TriggerFormData = {
  name: "",
  description: "",
  entityType: TriggerEntityType.Accommodation,
  active: true,
  conditions: [],
  emailTemplateId: "",
  recipients: [],
}

export function TriggerForm({
  triggerId,
  onSuccess,
  onBack,
}: {
  triggerId: string | null
  onBack: () => void
  onSuccess: () => void
}) {
  const [formData, setFormData] = useState<TriggerFormData>(initialFormData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newRecipient, setNewRecipient] = useState<Recipient>({
    type: "entity",
    value: "",
  })
  const [filteredTemplates, setFilteredTemplates] = useState<EmailTemplate[]>([])

  const { data: trigger, isLoading: isLoadingTrigger } = useGetTrigger(triggerId || "", {
    enabled: !!triggerId,
  })

  const { data: emailTemplates, isLoading: isLoadingTemplates } = useGetEmailTemplates()

  const { mutate: createTrigger } = useCreateTrigger({
    onSuccess: () => {
      setIsSubmitting(false)
      onSuccess()
    },
  })

  const { mutate: updateTrigger } = useUpdateTrigger({
    onSuccess: () => {
      setIsSubmitting(false)
      onSuccess()
    },
  })

  useEffect(() => {
    if (trigger) {


      setFormData({
        name: trigger.name,
        description: trigger.description || "",
        entityType: trigger.entityType,
        active: trigger.active,
        conditions: trigger.conditions as Condition[],
        emailTemplateId: trigger.emailTemplateId,
        recipients: trigger.recipients || [],
      })
    }
  }, [trigger])

  useEffect(() => {

    // Filter email templates based on entity type
    if (emailTemplates) {
      const filtered = emailTemplates.filter((template) => {
        const [entityType] = template.type.split(":")
        return entityType === formData.entityType
      })
      setFilteredTemplates(filtered)

      // If current template is not valid for this entity type, reset it
      if (formData.emailTemplateId && !filtered.some((t) => t.id === formData.emailTemplateId)) {
        setFormData((prev) => ({
          ...prev,
          emailTemplateId: "",
        }))
      }
    }
  }, [formData.entityType, emailTemplates])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSwitchChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, active: checked }))
  }

  const handleConditionsChange = (conditions: Condition[]) => {
    setFormData((prev) => ({
      ...prev,
      conditions,
    }))
  }

  const handleNewRecipientChange = (field: string, value: string) => {
    setNewRecipient((prev) => ({ ...prev, [field]: value }))
  }

  const addRecipient = () => {
    if (newRecipient.type && newRecipient.value) {
      setFormData((prev) => ({
        ...prev,
        recipients: [...prev.recipients, { ...newRecipient }],
      }))
      setNewRecipient({
        type: "entity",
        value: "",
      })
    }
  }

  const removeRecipient = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Prepare data for submission - remove ids from conditions
    const submissionData = {
      ...formData,
      conditions: formData.conditions.map(({...rest }) => rest),
    }

    if (triggerId) {


      const updatedTrigger = {
        id: triggerId,
        ...submissionData,
        createdAt: trigger?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Trigger;

      // For update, we need to include createdAt and updatedAt from the original trigger
      updateTrigger(updatedTrigger)
    } else {
      // For create, convert recipients to the expected format
      createTrigger({
        name: submissionData.name,
        active: submissionData.active,
        entityType: submissionData.entityType,
        emailTemplateId: submissionData.emailTemplateId,
        description: submissionData.description,
        conditions: submissionData.conditions as Condition[],
        recipients: submissionData.recipients.map((r) => ({
          type: r.type,
          value: r.value,
        })),
      })
    }
  }

  if (isLoadingTrigger) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          <T tKey="common.back" />
        </Button>
        <h2 className="text-2xl font-bold">
          {triggerId ? <T tKey="operations.triggers.edit.title" /> : <T tKey="operations.triggers.new.title" />}
        </h2>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <LoadingSpinner className="h-4 w-4" />
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              <T tKey="common.save" />
            </>
          )}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>
              <T tKey="operations.triggers.details" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  <T tKey="operations.triggers.fields.name" />
                </Label>
                <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required />
              </div>
              <div className="space-y-2 flex items-center">
                <div className="flex-1">
                  <Label htmlFor="active" className="mr-2">
                    <T tKey="operations.triggers.fields.active" />
                  </Label>
                </div>
                <Switch id="active" checked={formData.active} onCheckedChange={handleSwitchChange} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">
                <T tKey="operations.triggers.fields.description" />
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="entityType">
                  <T tKey="operations.triggers.fields.entityType" />
                </Label>
                <Select
                  value={formData.entityType}
                  onValueChange={(value) => handleSelectChange("entityType", value as TriggerEntityType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <T tKey="operations.triggers.conditions.add" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ConditionBuilder
              entityType={formData.entityType}
              conditions={formData.conditions}
              onChange={handleConditionsChange}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <T tKey="operations.triggers.emailTemplate" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingTemplates ? (
              <div className="flex justify-center p-4">
                <LoadingSpinner />
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center p-4 border rounded-md bg-gray-50">
                <p className="text-gray-500">
                  <T
                    tKey="operations.triggers.noTemplatesForEntity"
                    args={{
                      entity: ENTITY_TYPES.find((t) => t.value === formData.entityType)?.label || formData.entityType,
                    }}
                  />
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="emailTemplateId">
                  <T tKey="operations.triggers.fields.emailTemplate" />
                </Label>
                <Select
                  value={formData.emailTemplateId}
                  onValueChange={(value) => handleSelectChange("emailTemplateId", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an email template" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <T tKey="operations.triggers.recipients" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="recipientType">
                  <T tKey="operations.triggers.fields.recipientType" />
                </Label>
                <Select value={newRecipient.type} onValueChange={(value) => handleNewRecipientChange("type", value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECIPIENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end space-x-2">
                <div className="flex-1">
                  <Label htmlFor="recipientValue">
                    {newRecipient.type === "entity" ? (
                      <T tKey="operations.triggers.fields.recipientField" />
                    ) : (
                      <T tKey="operations.triggers.fields.recipientEmail" />
                    )}
                  </Label>
                  {newRecipient.type === "entity" ? (
                    <Select
                      value={newRecipient.value}
                      onValueChange={(value) => handleNewRecipientChange("value", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent>
                        {RECIPIENT_FIELD_OPTIONS.map((field) => (
                          <SelectItem key={field.name} value={field.name}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="recipientValue"
                      value={newRecipient.value}
                      onChange={(e) => handleNewRecipientChange("value", e.target.value)}
                      placeholder="email@example.com"
                      type="email"
                    />
                  )}
                </div>
                <Button type="button" onClick={addRecipient} className="mb-0.5">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {formData.recipients.length === 0 ? (
              <div className="text-center p-4 border rounded-md bg-gray-50">
                <p className="text-gray-500">
                  <T tKey="operations.triggers.noRecipients" />
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {formData.recipients.map((recipient, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-md bg-gray-50">
                    <div>
                      <span className="font-medium">
                        {RECIPIENT_TYPES.find((t) => t.value === recipient.type)?.label}:{" "}
                      </span>
                      {recipient.type === "entity" ? (
                        <span>
                          {RECIPIENT_FIELD_OPTIONS.find((f) => f.name === recipient.value)?.label || recipient.value}
                        </span>
                      ) : (
                        <span>{recipient.value}</span>
                      )}
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeRecipient(index)}>
                      <Trash className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
